import React from "react";
import { Button, ButtonProps } from "@mui/material";
import { EventFormState, EventTypeOption } from "../../eventMockData";
import type { EventAddressOption } from "./EventAddressSelector";
// cpaGold/cpaGoldDark come from the shared dashboard formStyles (both it and
// eventSalesShared alias commonStyles, so this re-point is zero-risk — B6/F).
import { cpaGold, cpaGoldDark } from "../../../../common/dashboard/formStyles";
import { eventDesignFont } from "../../eventDesignTheme";

export type WizardAction = "draft" | "publish" | "save" | "schedule";

export type EventTypeLibraryHandlers = {
  onAddEventType?: (label: string, color: string) => Promise<EventTypeOption | null>;
  onArchiveEventType?: (eventTypeId: string) => Promise<void>;
};

export type WizardStepProps = EventTypeLibraryHandlers & {
  event: EventFormState;
  setEvent: (updater: (prev: EventFormState) => EventFormState) => void;
  eventTypes: EventTypeOption[];
  chamberId: string;
  eventAddressOptions: readonly EventAddressOption[];
  errors: Record<string, string>;
  // The event is a Duplicate Event copy that still needs its own title/code.
  duplicated?: boolean;
  /**
   * Update Event only (design 16, CEO Oct 12 2026): a ticket type or sponsor
   * level pop-up was saved, so the wizard saves the event at once (the same
   * save as the footer button). The step then shows no success of its own.
   */
  onDialogSaved?: () => void;
};

// EV-N2 design notice (Create Event Screen.dc.html ~2397): shown while a
// duplicated event has no Event Title. Event Code is optional, so a blank
// code does not keep the notice open.
export const shouldShowDuplicateEventNotice = (
  event: Pick<EventFormState, "title">,
  duplicated?: boolean
): boolean => Boolean(duplicated) && !event.title.trim();

// "This is a Community/Member Event" can change only on a Draft with no sales.
// The API has the same rule and also checks imported sale history.
export const isEventKindLocked = (
  event: Pick<EventFormState, "updatedAtUtcMilli" | "status" | "attendeeCount" | "ticketRevenue" | "sponsorshipRevenue">
): boolean =>
  !!event.updatedAtUtcMilli &&
  (event.status !== "Draft" || event.attendeeCount > 0 || event.ticketRevenue > 0 || event.sponsorshipRevenue > 0);

// Wizard row ids are persisted with the event and reloaded in later sessions.
// A bare counter restarts at 0 on every page load, so a freshly added row
// (e.g. "speaker-1") collides with a persisted row of the same id and edits
// override each other (EVT-CREATE-006/007). The per-load random session
// segment keeps ids unique across page loads.
const wizardIdSession = Math.random().toString(36).slice(2, 10);
let wizardIdCounter = 0;
export const wizardId = (prefix: string): string => {
  wizardIdCounter += 1;
  return `${prefix}-${wizardIdSession}-${wizardIdCounter}`;
};

export const normalizeWizardTimeInput = (value: string): string => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "";

  const twentyFourHourMatch = trimmedValue.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (twentyFourHourMatch) {
    return `${twentyFourHourMatch[1].padStart(2, "0")}:${twentyFourHourMatch[2]}`;
  }

  const twelveHourMatch = trimmedValue.match(/^(\d{1,2})(?::([0-5]\d))?\s*([ap])\.?m\.?$/i);
  if (!twelveHourMatch) return trimmedValue;

  const rawHours = Number(twelveHourMatch[1]);
  if (rawHours < 1 || rawHours > 12) return trimmedValue;

  const minutes = twelveHourMatch[2] || "00";
  const meridiem = twelveHourMatch[3].toLowerCase();
  const hours =
    meridiem === "p"
      ? rawHours === 12
        ? 12
        : rawHours + 12
      : rawHours === 12
        ? 0
        : rawHours;

  return `${String(hours).padStart(2, "0")}:${minutes}`;
};

export const normalizeEventDateTimeFields = (
  event: EventFormState
): EventFormState => ({
  ...event,
  endDate: event.endDate || event.startDate,
  startTime: normalizeWizardTimeInput(event.startTime),
  endTime: normalizeWizardTimeInput(event.endTime),
  // Fully-empty agenda rows are add-row scaffolding only; drop them so they
  // never reach validation or the backend (EVT-PUBLISH-001).
  agendaItems: (event.agendaItems || []).filter(
    (agendaItem) => agendaItem.timeLabel.trim() || agendaItem.details.trim()
  ),
});

// WizardSection was deleted in the B6 polish pass: all five call sites
// (Step2 x2, Step3 x2, Step4 x1) now use the shared
// common/dashboard/SectionShell (variant="pill", Q-037 normalization).

// Step 1 section card and heading (Claude Design (8) Create Event
// Screen.dc.html:87-92, 266-270, 322-326, 412-416, 453-456): bordered 12px
// card, 28px padding, 14px/600 heading with 13px #0A0A0A helper lines.
export const WizardSectionCard = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement => (
  <section
    className={`rounded-[12px] border border-[#ECECEC] bg-white p-[20px] shadow-[0_1px_2px_rgba(0,0,0,0.04)] md:p-[28px] ${className}`}
  >
    {children}
  </section>
);

export const WizardSectionHeading = ({
  title,
  required,
  lines = [],
  className = "",
}: {
  title: string;
  required?: boolean;
  lines?: string[];
  className?: string;
}): React.ReactElement => (
  <div className={className}>
    <h3 className="text-[14px] font-semibold leading-[normal] text-[#0A0A0A]">
      {title}
      {required ? <span className="text-[#F0202E]"> *</span> : null}
    </h3>
    {lines.map((line) => (
      <p key={line} className="mt-[4px] text-[13px] leading-[normal] text-[#0A0A0A]">
        {line}
      </p>
    ))}
  </div>
);

export const FieldLabel = ({
  children,
  required,
  hint,
  compact = true,
  note,
  noteClassName = "font-normal text-[#727272]",
  className = "mb-3",
  hintClassName,
  fontSize,
}: {
  children: React.ReactNode;
  required?: boolean;
  hint?: string;
  compact?: boolean;
  note?: string;
  noteClassName?: string;
  className?: string;
  hintClassName?: string;
  fontSize?: number;
}): React.ReactElement => (
  <div className={className}>
    <span style={fontSize ? { fontSize } : undefined} className={`${compact ? "text-[14px] leading-5" : "text-[16px] leading-6"} font-semibold text-[#0A0A0A]`}>
      {children}
      {required ? <span className="text-red-500"> *</span> : null}
      {note ? <span className={noteClassName}> {note}</span> : null}
    </span>
    {hint ? <p className={hintClassName || (compact ? "mt-1 text-[13px] leading-5 text-[#0A0A0A]" : "mt-2 text-[15px] leading-6 text-[#0A0A0A]")}>{hint}</p> : null}
  </div>
);

type WizardActionTone = "black" | "gold" | "red" | "green" | "outline";

const wizardActionToneSx: Record<WizardActionTone, ButtonProps["sx"]> = {
  black: {
    backgroundColor: "#000",
    color: "#fff",
    "&:hover": { backgroundColor: "#111827" },
  },
  gold: {
    backgroundColor: cpaGold,
    color: "#fff",
    "&:hover": { backgroundColor: cpaGoldDark },
  },
  red: {
    backgroundColor: "#ff1728",
    color: "#fff",
    "&:hover": { backgroundColor: "#d90f1e" },
  },
  green: {
    // #12bd7b is the mock-verified "Schedule to Publish" green (Q-006 /
    // screenshot parity) — do NOT normalize it to cmpGreen #10B981.
    backgroundColor: "#12bd7b",
    color: "#fff",
    "&:hover": { backgroundColor: "#0ea66b" },
  },
  outline: {
    borderColor: "#d1d5db",
    color: "#1f2937",
    "&:hover": {
      borderColor: "#9ca3af",
      backgroundColor: "#f9fafb",
    },
  },
};

export const WizardActionButton = ({
  tone = "gold",
  sx,
  children,
  ...props
}: ButtonProps & {
  tone?: WizardActionTone;
  // Allow rendering as a router link (e.g. the wizard Cancel button) so the
  // shared unsaved-changes anchor interception still applies.
  component?: React.ElementType;
  to?: string;
}): React.ReactElement => (
  <Button
    {...props}
    variant={tone === "outline" ? "outlined" : "contained"}
    sx={[
      {
        borderRadius: "6px",
        boxShadow: "none",
        fontSize: props.size === "small" ? 11 : 14,
        letterSpacing: 0,
        minWidth: 0,
        fontFamily: eventDesignFont,
        fontWeight: 600,
        lineHeight: 1.2,
        minHeight: props.size === "small" ? 27 : 44,
        px: props.size === "small" ? 1.4 : 2.8,
        textTransform: "none",
        whiteSpace: "nowrap",
        "&:hover": { boxShadow: "none" },
        "& .MuiButton-startIcon": {
          mr: 0.75,
          "& svg": { fontSize: props.size === "small" ? 15 : 22 },
        },
        "& .MuiButton-endIcon": {
          ml: 1,
          "& svg": { fontSize: props.size === "small" ? 15 : 24 },
        },
      },
      wizardActionToneSx[tone],
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
  >
    {children}
  </Button>
);

export const wizardTextFieldSx = {
  "& .MuiInputBase-root": {
    borderRadius: "8px",
    fontFamily: eventDesignFont,
    fontSize: 14,
    minHeight: 42,
  },
  "& .MuiInputBase-input": {
    paddingBottom: "10px",
    paddingTop: "10px",
  },
};

// Wizard-skin overrides for the shared CurrencyInput (POL-005): reproduce the
// 58px / 16px MUI-outline look of wizardTextFieldSx so the popup price fields
// do not shrink to CurrencyInput's h-11 dashboard default. rounded-md = the
// 6px radius wizardTextFieldSx uses.
export const wizardCurrencyInputClassName =
  "h-[58px] w-full rounded-md border border-gray-300 bg-white pl-8 pr-4 text-[16px] text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100";
// Design CES:1380/1450: "$" 14px/600 #0A0A0A, 13px from the left, centred
// on the 42px input (top 21px, so a taller grid cell cannot push it down;
// Monday 13291586291).
export const wizardCurrencyAdornmentClassName =
  "pointer-events-none absolute left-[13px] top-[21px] -translate-y-1/2 text-[14px] font-semibold leading-none text-[#0A0A0A]";

// Header row of the ticket and sponsor dialogs (Create Event Screen.dc.html
// ~1325-1333 and ~1403-1411): title and subtitle on the left, 28px red close
// circle in the same flex row.
export const WizardDialogHeader = ({
  title,
  subtitle,
  onClose,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
}): React.ReactElement => (
  <div className="flex items-start justify-between gap-[16px]">
    <div className="flex flex-col gap-[3px]">
      <h3 className="m-0 text-[18px] font-bold leading-tight text-[#0A0A0A]">{title}</h3>
      <p className="m-0 text-[13px] text-[#0A0A0A]">{subtitle}</p>
    </div>
    <button
      type="button"
      aria-label="close"
      onClick={onClose}
      className="flex h-[28px] w-[28px] shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-[#F21B1B] p-0 hover:bg-[#D01414]"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" aria-hidden="true" style={{ width: 13, height: 13 }}>
        <path d="m6 6 12 12M18 6 6 18" />
      </svg>
    </button>
  </div>
);
